import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { API_URLS } from 'src/api/apiUrls';
import useApiServices from 'src/api/useApiServices';
import {
  useAddAdsMutation,
  useEditAdsMutation,
  useHearingCouncilsQuery,
  useOneFaqQuery,
  useSessionsAnalysisQuery,
} from 'src/graphql';
import { useBoolean } from 'src/hooks/use-boolean';
import { useLocales } from 'src/locales';
import useLangsWithErrors from 'src/routes/hooks/use-langs-with-errors';
import { paths } from 'src/routes/paths';
import useSchemas from 'src/schemas/hooks/useSchemas';
import { AddADSVariablesType, AdsDto } from 'src/types/ads';
import { getLocalizedValue } from 'src/utils/get-localized-value';

const useAddNewADS = () => {
  // #region States
  const confirm = useBoolean();
  const router = useNavigate();
  const { AddAdsSchema } = useSchemas();
  const { adsId: adsIdPrm } = useParams();
  const queryClient = useQueryClient();
  const { allLangs } = useLocales();
  const { usePostApi } = useApiServices();
  const [currentTabValue, setCurrentTabValue] = useState(allLangs[0].value);
  const [adsId, setAdsId] = useState<string | undefined>(adsIdPrm);
  const [file, setFile] = useState<File | string | null>(null);
  const { state }: { state: { ads: AdsDto | undefined } | undefined } = useLocation();
  const isEditing = !!adsIdPrm;
  const [dataToSend, setDataToSend] = useState<AddADSVariablesType>();
  // #endregion States

  let defaultValues: AddADSVariablesType = {
    adBy: state?.ads?.adBy || '',
    refId: state?.ads?.refId || '',
    type: state?.ads?.type || '',
    startDate: state?.ads?.startDate!,
    endDate: state?.ads?.endDate!,
    active: state?.ads?.active || false,
    description: state?.ads?.description || '',
    mediaUrl: state?.ads?.mediaUrl || '',
  };

  const methods = useForm<AddADSVariablesType>({
    defaultValues,
    resolver: yupResolver(AddAdsSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = useCallback(
    async (data: AddADSVariablesType) => {
      const newData = { ...data };
      if (!isEditing) delete (newData as any).mediaUrl;
      newData.active ?? false;
      if (newData?.type == 'notSelection') newData.type = undefined;
      setDataToSend(newData);
      confirm.onTrue();
    },
    [confirm]
  );

  const langsWithErrors = useLangsWithErrors(errors);
  // #endregion form

  // Get Sessions
  const { data: sessions, isLoading: isLoadingSessions } = useSessionsAnalysisQuery({
    where: {
      status: { _neq: 'Expired' },
    },
  });

  const sessionsOpts =
    sessions?.sessions.map((session) => ({
      label: getLocalizedValue(session.title),
      value: session.id!,
      startDate: session.startDate,
      endDate: session.expiryDate,
    })) || [];

  // #region Services
  const { data: hearingCouncils, isLoading: isLoadingHearingCouncils } = useHearingCouncilsQuery({
    status: { _neq: 'Expired' },
  });

  const hearingCouncilsOpts =
    hearingCouncils?.hearing_councils.map((hearing_council) => ({
      label: getLocalizedValue(hearing_council.sessionName),
      value: hearing_council.hearingCouncilId!,
      startDate: hearing_council.startDate,
      endDate: hearing_council.expiryDate,
    })) || [];

  const {
    mutate: uploadImage,
    isLoading: isUploadingImage,
    data,
  } = usePostApi({
    url: API_URLS.UPLOAD_Ads_Image(adsId ?? ''),
    withFormData: true,
    onSuccess: (data: any) => {
      methods.setValue('mediaUrl', data?.mediaUrl);
      if (!isEditing) router(paths.dashboard.contentManagement.ads.root);
    },
  });

  const {
    mutate: addAds,
    isLoading: isAdding,
    isSuccess: hasAddedBase,
  } = useAddAdsMutation({
    onSuccess: (data) => {
      setAdsId(data.insert_ads_one?.id);
      confirm.onFalse();
      if (!file) router(paths.dashboard.contentManagement.ads.root);
    },
  });

  const { mutate: editAds, isLoading: isUpdating } = useEditAdsMutation({
    onSuccess: (data) => {
      if (confirm.value) {
        confirm.onFalse();
        queryClient.invalidateQueries({ queryKey: ['ADS'] });
        router(paths.dashboard.contentManagement.ads.root);
      }
    },
  });

  const mutate = isEditing
    ? () =>
        editAds({
          ...dataToSend,
          id: adsId,
        })
    : () =>
        addAds({
          ...dataToSend,
        });
  // #endregion Services

  // #region handlers
  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTabValue(newValue);
  }, []);
  // #endregion handlers

  // #region handlers
  const handleDropSingleFile = useCallback((acceptedFiles: File[]) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setFile(
        Object.assign(newFile, {
          preview: URL.createObjectURL(newFile),
        })
      );
      methods.setValue('mediaUrl', URL.createObjectURL(newFile));
    }
  }, []);

  useEffect(() => {
    if (file && (hasAddedBase || state?.ads)) uploadImage({ file: file });
  }, [file, hasAddedBase]);
  // #endregion useEffect

  return {
    editAds,
    adsId: adsIdPrm,
    methods,
    handleSubmit,
    onSubmit,
    currentTabValue,
    file,
    setFile,
    uploadImage,
    isUploadingImage,
    handleDropSingleFile,
    mutate,
    isEditing,
    confirm,
    isAdding,
    isUpdating,
    handleChangeTab,
    langsWithErrors,
    sessionsOpts,
    isLoadingSessions,
    hearingCouncilsOpts,
    isLoadingHearingCouncils,
  };
};

export default useAddNewADS;
